import * as React from "react"
import { VercelIntegrationWizard } from "@modules/cdn/vercel"
import { NetlifyIntegrationWizard } from "@modules/cdn/netlify"
import { FastlyIntegrationForm } from "@modules/cdn/fastly"
import { AmazonS3IntegrationForm } from "@modules/cdn/amazonS3"
import { AzureIntegrationForm } from "@modules/cdn/azure"
import { GCloudStorageIntegrationForm } from "@modules/cdn/gcloudStorage"
import { FirebaseIntegrationWizard } from "@modules/cdn/firebase"
import {
  StyledPanelBodySection,
  StyledPanelBoilerplate,
  useShowSuccessToast,
} from "gatsby-interface"
import { CdnIntegration, CdnVendor } from "@modules/graphql/types"
import { CdnIntegrationSharedProps } from "../types"
import {
  ErrorAlert,
  useTriggerErrorAlert,
} from "@modules/ui/components/ErrorAlert"
import { cdnIntegration as cdnIntegrationText } from "@modules/locales/default.json"
import { cdnVendorNames } from "../constants"
import { interpolateMessage } from "@modules/locales"

const integrationFormComponents: Record<
  CdnVendor,
  React.ComponentType<CdnIntegrationSharedProps> | null
> = {
  NETLIFY: NetlifyIntegrationWizard,
  FASTLY: FastlyIntegrationForm,
  FIREBASE: FirebaseIntegrationWizard,
  S3: AmazonS3IntegrationForm,
  GCS: GCloudStorageIntegrationForm,
  NOW: VercelIntegrationWizard,
  AZURE: AzureIntegrationForm,
  CLOUD_CDN: null,
}

const integrationTitles: Record<CdnVendor, string> = {
  AZURE: cdnIntegrationText.headers.azureIntegrationTitle,
  CLOUD_CDN: ``,
  GCS: cdnIntegrationText.headers.gcloudStorageIntegrationTitle,
  FASTLY: cdnIntegrationText.headers.fastlyIntegrationTitle,
  FIREBASE: cdnIntegrationText.headers.firebaseIntegrationTitle,
  NETLIFY: cdnIntegrationText.headers.netlifyIntegrationTitle,
  NOW: cdnIntegrationText.headers.vercelIntegrationTitle,
  S3: cdnIntegrationText.headers.amazonS3IntegrationTitle,
}

export type CdnIntegrationModalProps = {
  siteId: string
  siteName: string
  cdn: CdnIntegration
  onClose: () => void
  onConnect: () => void
}

export function CdnIntegrationModal({
  siteId,
  siteName,
  cdn,
  onClose,
  onConnect,
}: CdnIntegrationModalProps) {
  const [setError, errorAlert] = useTriggerErrorAlert()
  const showSuccessToast = useShowSuccessToast()

  const IntegrationForm = integrationFormComponents[cdn.vendor]
  const vendorName = cdnVendorNames[cdn.vendor]

  return (
    <StyledPanelBoilerplate
      aria-label={interpolateMessage<"cdnVendorName">(
        cdnIntegrationText.headers.authorizeCdn,
        { cdnVendorName: vendorName }
      )}
      isOpen={Boolean(cdn)}
      onClose={onClose}
      header={integrationTitles[cdn.vendor]}
    >
      <StyledPanelBodySection>
        {errorAlert}
        {IntegrationForm ? (
          <IntegrationForm
            siteId={siteId}
            siteName={siteName}
            integration={cdn}
            onCancel={onClose}
            onSetCDNConfig={({ success }) => {
              if (success) {
                onConnect()
                onClose()
                return showSuccessToast(
                  cdnIntegrationText.messages.cdnIntegrationUpdated
                )
              }

              return setError(new Error(`Something went wrong`))
            }}
            onSubmitFailed={err => setError(err)}
          />
        ) : (
          <ErrorAlert>
            {interpolateMessage<"cdnVendorName">(
              cdnIntegrationText.messages.vendorNotSupported,
              { cdnVendorName: vendorName }
            )}
          </ErrorAlert>
        )}
      </StyledPanelBodySection>
    </StyledPanelBoilerplate>
  )
}
